import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { Intake } from "./Intake";
import { AgentCase } from "./AgentCase";
import { Resume } from "./Resume";
import { Builder } from "./Builder";
import { Landing } from "./Landing";
import { UnderConstruction } from "./UnderConstruction";
import { Account } from "./Account";
import { Dashboard } from "./Dashboard";
import { Home } from "./Home";
import { PageEditor } from "./PageEditor";
import { SetupEditor } from "./SetupEditor";
import { SignUp } from "./SignUp";
import "./styles.css";

/**
 * Routing, such as it is.
 *
 * Six surfaces, told apart by the first path segment:
 *
 *   /                          closed while we rebuild (was: the pitch)
 *   /signup                    a business making an account
 *   /account/*                 signing in, or choosing a password
 *   /dashboard                 the account's home: inbox, credit, chats
 *   /dashboard/<slug>          one of them
 *   /dashboard/<slug>/page     ...its look
 *   /dashboard/<slug>/setup    ...what crAIg knows on it
 *   /app/case/<id>?k=<token>   the practice, arriving from an email link
 *   /resume/<id>?k=<token>     a customer coming back to answer a question
 *   /<slug>                    a customer, at their business's intake
 *
 * That's the whole router. A routing library for six entry points would be
 * more machinery than the problem deserves.
 *
 * The root used to fall through to `<Intake slug="demo">`, which meant
 * letcraig.ai greeted every visitor by starting a tax return with a
 * business that doesn't exist.
 */
const segments = window.location.pathname.split("/").filter(Boolean);
const token = new URLSearchParams(window.location.search).get("k") ?? "";
const root = createRoot(document.getElementById("root")!);

if (segments[0] === "app" && segments[1] === "case" && segments[2]) {
  root.render(
    <StrictMode>
      <AgentCase caseId={segments[2]} token={token} />
    </StrictMode>,
  );
} else if (segments[0] === "resume" && segments[1]) {
  root.render(
    <StrictMode>
      <Resume caseId={segments[1]} token={token} />
    </StrictMode>,
  );
} else if (segments[0] === "account") {
  // /account/sign-in · /account/forgot · /account/password?k=…
  const mode = segments[1] === "password" ? "password" : segments[1] === "forgot" ? "forgot" : "sign-in";
  root.render(
    <StrictMode>
      <Account mode={mode} />
    </StrictMode>,
  );
} else if (segments[0] === "build") {
  /*
    The two-panel builder: the settings on the left, crAIg on the right,
    both writing to one case.

    On its own address while it is being built, rather than replacing
    /start — that is still the only way anybody can finish a setup and put
    a chat live, and swapping it for a screen that cannot yet do the
    signing would leave no working path at all. It takes /start's place
    once it can finish the job.
  */
  root.render(
    <StrictMode>
      <Builder slug="start" />
    </StrictMode>,
  );
} else if (segments[0] === "signup") {
  root.render(
    <StrictMode>
      <SignUp />
    </StrictMode>,
  );
} else if (segments[0] === "dashboard") {
  /*
    /dashboard                  every page this account has
    /dashboard/<slug>           one page
    /dashboard/<slug>/page      ...its look
    /dashboard/<slug>/setup     ...what crAIg knows on it

    The slug is in the URL because an account can hold several and the
    session no longer says which. The server checks it belongs to them on
    every call — see `page()` in routes/dashboard.ts.
  */
  const slug = segments[1];
  root.render(
    <StrictMode>
      {!slug ? (
        <Home />
      ) : segments[2] === "page" ? (
        <PageEditor slug={slug} />
      ) : segments[2] === "setup" ? (
        <SetupEditor page={slug} />
      ) : (
        <Dashboard page={slug} />
      )}
    </StrictMode>,
  );
} else if (!segments[0]) {
  /*
    Closed for the rebuild.

    This branch renders <Landing /> when we are open — the pitch, 365
    lines of it, still sitting in Landing.tsx untouched and still imported
    above so nothing rots while it waits. Opening again is this one
    identifier, and nothing else.

    Only the root is shut. Every other address still works, deliberately:
    the dashboard, signing in, and every business's live intake at
    /<slug>. Those pages belong to the practices that bought them.
  */
  root.render(
    <StrictMode>
      <UnderConstruction />
    </StrictMode>,
  );
} else {
  root.render(
    <StrictMode>
      <Intake
        slug={segments[0]}
        // Only ever set by a link someone was given — a business pointing
        // its Vietnamese-speaking customers at /slug?lang=vi. Everyone else
        // is asked on arrival, once.
        {...(new URLSearchParams(window.location.search).get("lang")
          ? { language: new URLSearchParams(window.location.search).get("lang")! }
          : {})}
      />
    </StrictMode>,
  );
}
